import { getIsAuthenticated } from '@/lib/msal-config'
import { queryClient } from '@/lib/query-client'
import { trackEvent, trackException } from '@/lib/telemetry'
import { PaymentMilestoneStatus, QuoteType } from '@/lib/types'
import { getQuoteId } from '@/lib/utils'
import { notifyManager, useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { z, ZodError } from 'zod'
import { dynamicsApi, proposalApi } from './client'
import { getMultipleRecords, globalOptionSetDefinitionSchema } from './helpers'
import { QUERY_KEYS } from './query-keys'
import { accountSchema } from './accounts'
import { contactSchema } from './accounts'
import { initializeQuoteDetailsCache, quoteDetailSchema } from './quote-details'
import { originalAgreementQuoteSchema, QuoteData, quoteSchema, useQuoteQuery } from './quotes'
import { accountRoomSchema } from './rooms-and-systems'

export const payLinkSchema = z.object({
  activityid: z.string({ message: 'PayLink activity ID is missing.' }),
  nodus_pflink: z.string({ message: 'PayLink URL is missing.' }),
  nodus_pfstatustext: z.string({ message: 'PayLink status is missing.' }),
  nodus_pfpaidon: z.string().nullable(),
})
export type PayLinkData = z.infer<typeof payLinkSchema>

// The change order that replaced (or, while it is Draft, reserved) a milestone.
export const replacingQuoteSchema = z.object({
  quoteid: z.string(),
  quotenumber: z.string().nullable().optional().default(null),
  name: z.string().nullable().optional().default(null),
  statecode: z.number().nullable().optional().default(null),
})
export type ReplacingQuoteData = z.infer<typeof replacingQuoteSchema>

export const paymentMilestoneSchema = z
  .object({
    dsy_paymentmilestoneid: z.string({ message: 'Payment milestone ID is missing.' }),
    '@odata.etag': z.string().optional(),
    dsy_name: z.string({ message: 'Payment milestone name is missing.' }),
    dsy_percent: z.number({ message: 'Payment milestone percentage is missing.' }).nullable(),
    dsy_amount: z.number({ message: 'Payment milestone amount is missing.' }).nullable(),
    dsy_sequencenumber: z
      .number({ message: 'Payment milestone sequence number is missing.' })
      .nullable()
      .transform((sequence) => sequence ?? 0),
    dsy_estimatedinvoicedate: z.string().nullable().optional().default(null),
    statuscode: z
      .nativeEnum(PaymentMilestoneStatus)
      .catch(PaymentMilestoneStatus.Open)
      .default(PaymentMilestoneStatus.Open),
    statecode: z
      .number()
      .nullable()
      .optional()
      .transform((statecode) => statecode ?? 0),
    _dsy_quote_value: z.string().nullable().optional().default(null),
    _dsy_salesorder_value: z.string().nullable().optional().default(null),
    _dsy_replacedbyquote_value: z.string().nullable().optional().default(null),
    dsy_replacedbyquote: replacingQuoteSchema.nullable().optional().default(null),
    _dsy_epayment_value: z.string().nullable().optional().default(null),
    dsy_invoiceissueddate: z.string().nullable().optional().default(null),
    dsy_quickbooksinvoiceid: z.string().nullable().optional().default(null),
    dsy_quickbookslastsyncerror: z.string().nullable().optional().default(null),
    dsy_paylink: payLinkSchema.nullable(),
  })
  .transform((milestone) => ({
    ...milestone,
    dsy_percent: milestone.dsy_percent ?? 0,
    dsy_amount: milestone.dsy_amount ?? 0,
    dsy_percent_was_null: milestone.dsy_percent === null,
  }))
export type PaymentMilestoneData = z.infer<typeof paymentMilestoneSchema>

export type CreatePaymentMilestoneData = {
  dsy_name: string
  dsy_percent: number
  dsy_amount: number
  dsy_sequencenumber: number
  'dsy_quote@odata.bind': string
}

const PAYMENT_MILESTONE_QUERY =
  '?$select=dsy_paymentmilestoneid,dsy_name,dsy_percent,dsy_amount,dsy_sequencenumber,dsy_estimatedinvoicedate,statecode,statuscode,_dsy_quote_value,_dsy_salesorder_value,_dsy_replacedbyquote_value,_dsy_epayment_value,dsy_invoiceissueddate,dsy_quickbooksinvoiceid,dsy_quickbookslastsyncerror' +
  '&$expand=dsy_paylink($select=activityid,nodus_pflink,nodus_pfstatustext,nodus_pfpaidon),dsy_replacedbyquote($select=quoteid,quotenumber,name,statecode)'

export function usePaymentMilestonesQuery() {
  return useQuery({
    queryKey: QUERY_KEYS.paymentMilestones,
    queryFn: async () => {
      const quoteId = getQuoteId()
      const paymentMilestones = await getMultipleRecords<PaymentMilestoneData>(
        'dsy_paymentmilestones',
        `${PAYMENT_MILESTONE_QUERY}&$filter=_dsy_quote_value eq '${quoteId}'&$orderby=dsy_sequencenumber asc`
      )
      return z.array(paymentMilestoneSchema).parse(paymentMilestones)
    },
  })
}

/** The milestones holding any of `ids`, with the quote each is linked to (null for an orphan). */
export async function findPaymentMilestoneHolders(ids: string[]) {
  const milestones = await getMultipleRecords<Pick<PaymentMilestoneData, 'dsy_paymentmilestoneid' | '_dsy_quote_value'>>(
    'dsy_paymentmilestones',
    `?$select=dsy_paymentmilestoneid,_dsy_quote_value&$filter=${ids.map((id) => `dsy_paymentmilestoneid eq ${id}`).join(' or ')}`
  )
  return milestones.map((milestone) => ({
    id: milestone.dsy_paymentmilestoneid.toLowerCase(),
    quoteId: milestone._dsy_quote_value?.toLowerCase() ?? null,
  }))
}

export function useSalesOrderPaymentMilestonesQuery() {
  const { data: quote } = useQuoteQuery()
  const salesOrderId = quote?.dsy_salesorder?.salesorderid

  return useQuery({
    queryKey: QUERY_KEYS.salesOrderPaymentMilestones(salesOrderId),
    queryFn: async () => {
      if (!salesOrderId) return []
      const milestones = await getMultipleRecords<PaymentMilestoneData>(
        'dsy_paymentmilestones',
        `${PAYMENT_MILESTONE_QUERY}&$filter=_dsy_salesorder_value eq '${salesOrderId}'&$orderby=dsy_sequencenumber asc`
      )
      return z.array(paymentMilestoneSchema).parse(milestones)
    },
    enabled: getIsAuthenticated() && !!salesOrderId && quote?.dsy_type === QuoteType.ChangeOrder,
  })
}

export function useProposalSignatureQuery() {
  return useQuery({
    queryKey: QUERY_KEYS.signature,
    queryFn: async () => {
      const quoteId = getQuoteId()
      // `?size=full` is required — default would return the 144x144 thumbnail,
      // which is too low-res for the rendered signature on the proposal/contract.
      const res = await dynamicsApi.get(`/quotes(${quoteId})/dsy_approvalsignature?size=full`)
      return z.string().optional().parse(res.data.value) ?? null
    },
    refetchOnWindowFocus: 'always',
  })
}

export const proposalSchema = z
  .object({
    quote: quoteSchema,
    quote_details: z.array(quoteDetailSchema),
    account: accountSchema,
    account_rooms: z.array(accountRoomSchema),
    contact: contactSchema,
    systems: globalOptionSetDefinitionSchema,
    system_scope_tags: globalOptionSetDefinitionSchema,
    payment_milestones: z.array(paymentMilestoneSchema),
    sales_order_payment_milestones: z.array(paymentMilestoneSchema).default([]),
    signature: z.string().nullable(),
    previous_quote: quoteSchema.nullable(),
    previous_quote_details: z.array(quoteDetailSchema).nullable(),
    // The change order's original quote for its agreement (useAgreement); clients can't query Dataverse for it
    original_agreement_quote: originalAgreementQuoteSchema.nullable().optional(),
  })
  .superRefine((proposal, ctx) => {
    if (proposal.quote.dsy_type === QuoteType.ChangeOrder) {
      if (!proposal.quote.dsy_salesorder?.salesorderid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Sales order is required for change orders.',
        })
      }
      if (!proposal.previous_quote) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Previous quote is required for change orders.',
        })
      }
      if (!proposal.previous_quote_details) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Previous quote details are required for change orders.',
        })
      }
    }
  })

export type ProposalData = z.infer<typeof proposalSchema>

let viewTracked = false
export async function trackProposalView() {
  if (viewTracked) return
  if (getIsAuthenticated()) return
  viewTracked = true
  try {
    const res = await proposalApi.post(`/proposal/${getQuoteId()}/track-view`)
    trackEvent('proposal_view', { outcome: res.status === 204 ? 'incremented' : `unexpected_${res.status}` })
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined
    if (status === 429) {
      trackEvent('proposal_view', { outcome: 'skipped_rate_limited' })
    } else if (status && status >= 400 && status < 500) {
      trackEvent('proposal_view', { outcome: 'skipped_bot' })
    } else {
      trackEvent('proposal_view', { outcome: 'error' })
      trackException(error, { context: 'proposal.trackProposalView' })
    }
  }
}

export function useInitializeProposal() {
  return useQuery({
    queryKey: QUERY_KEYS.proposal,
    queryFn: async () => {
      const quoteId = getQuoteId()
      // _retryable: false — retry is handled at the query level (retry: 1 below);
      // without it the axios interceptor's 3 network retries multiply per attempt.
      const res = await proposalApi.get<ProposalData>(`/proposal/${quoteId}`, { _retryable: false })

      let parsed: ProposalData
      try {
        parsed = proposalSchema.parse(res.data)
      } catch (error) {
        if (error instanceof ZodError) {
          trackException(error, { context: 'proposal.useInitializeProposal.validation' })
          throw new Error(
            'We were unable to load your proposal. Please contact your sales representative for assistance.'
          )
        }
        throw error
      }

      const {
        quote,
        quote_details,
        account,
        account_rooms,
        contact,
        systems,
        system_scope_tags,
        payment_milestones,
        sales_order_payment_milestones,
        signature,
        previous_quote,
        previous_quote_details,
        original_agreement_quote,
      } = parsed

      notifyManager.batch(() => {
        queryClient.setQueryData(QUERY_KEYS.quote(), quote)
        queryClient.setQueryData(QUERY_KEYS.quote({ previousQuote: true }), previous_quote)
        // Missing from an older API: the change order shows its own agreement, which that API checks against
        queryClient.setQueryData(
          QUERY_KEYS.originalAgreementQuote(quote.dsy_salesorder?.salesorderid),
          original_agreement_quote ?? null
        )

        initializeQuoteDetailsCache({
          allQuoteDetails: quote_details,
          rooms: account_rooms,
          systems: systems,
        })
        if (previous_quote_details) {
          initializeQuoteDetailsCache({
            allQuoteDetails: previous_quote_details,
            previousQuote: true,
            rooms: account_rooms,
            systems: systems,
          })
        }

        queryClient.setQueryData(QUERY_KEYS.signature, signature)
        queryClient.setQueryData(QUERY_KEYS.account, account)
        queryClient.setQueryData(QUERY_KEYS.primaryContact, contact)
        queryClient.setQueryData(QUERY_KEYS.accountRooms, account_rooms)
        queryClient.setQueryData(QUERY_KEYS.systems, systems)
        queryClient.setQueryData(QUERY_KEYS.systemScopeTags, system_scope_tags)
        queryClient.setQueryData(QUERY_KEYS.paymentMilestones, payment_milestones)
        queryClient.setQueryData(
          QUERY_KEYS.salesOrderPaymentMilestones(quote.dsy_salesorder?.salesorderid),
          sales_order_payment_milestones
        )
      })

      return true
    },
    refetchOnWindowFocus: 'always',
    retry: (failureCount, error) => {
      // Deliberate 4xx gates (draft/closed/not found) never succeed on retry
      const status = isAxiosError(error) ? error.response?.status : undefined
      if (status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429) return false
      return failureCount < 1
    },
  })
}

export async function approveProposal({
  fullName,
  base64Data,
  acceptedTerms,
  agreementId,
}: {
  fullName: string
  base64Data: string | null
  acceptedTerms: boolean
  // The agreement on screen; the API refuses a signature whose agreement differs at approval time
  agreementId: string
}) {
  const quoteId = getQuoteId()
  const res = await proposalApi.post(`/proposal/${quoteId}/approve`, {
    fullName,
    base64Data,
    acceptedTerms,
    agreementId,
  })
  try {
    return payLinkSchema.nullable().parse(res.data)
  } catch (error) {
    if (error instanceof ZodError) {
      trackException(error, { context: 'proposal.approveProposal.validation' })
      throw new Error('Your approval was submitted but we encountered an issue. Please refresh the page.')
    }
    throw error
  }
}

export function quoteStatusHeaders(quote: Pick<QuoteData, 'quoteid' | '@odata.etag'>) {
  const etag = quote['@odata.etag']
  if (!etag || !/^W\/"[0-9]+"$/.test(etag)) {
    throw new Error('Refresh the quote before changing its status.')
  }
  return { 'If-Match': etag }
}

export async function activateQuoteRequest(quote: Pick<QuoteData, 'quoteid' | '@odata.etag'>) {
  const res = await proposalApi.post(`/proposal/${quote.quoteid}/activate`, undefined, { headers: quoteStatusHeaders(quote) })
  return z
    .object({
      correctedMilestoneCount: z.number().int().nonnegative(),
      protectedMilestoneCount: z.number().int().nonnegative(),
      warnings: z.array(z.string()).default([]),
    })
    .parse(res.data)
}

const milestoneReplacementsSchema = z.object({
  milestone_ids: z.array(z.string()),
  replaced_total: z.number(),
})

/** Sets which sales-order milestones this Draft change order replaces (the
 * server validates eligibility and clears reservations not in the list).
 * 422 = an ineligible row (detail is user-facing); 409/412 = stale quote. */
export async function saveMilestoneReplacementsRequest(
  quote: Pick<QuoteData, 'quoteid' | '@odata.etag'>,
  milestoneIds: string[]
) {
  const res = await proposalApi.post(
    `/quote/${quote.quoteid}/milestone-replacements`,
    { milestone_ids: milestoneIds },
    { headers: quoteStatusHeaders(quote) }
  )
  return milestoneReplacementsSchema.parse(res.data)
}

const activationConflictsSchema = z.object({
  conflicts: z.array(
    z.object({
      quoteid: z.string().nullable(),
      quotenumber: z.string().nullable(),
      statecode: z.number().nullable(),
      statuscode: z.number().nullable(),
      globalIds: z.array(z.string()),
      names: z.array(z.string()),
    })
  ),
  message: z.string().nullable(),
})

export type ActivationConflicts = z.infer<typeof activationConflictsSchema>

/** Sibling change orders (Active or Won-not-Applied) that edit the same lines as this one.
 * Pre-check only: the server re-runs the same rule inside activation. */
export async function getChangeOrderActivationConflicts(quoteId: string) {
  const res = await proposalApi.get(`/change-order/${quoteId}/activation-conflicts`)
  return activationConflictsSchema.parse(res.data)
}

export async function createPaylinkForFirstMilestone() {
  const quoteId = getQuoteId()
  const res = await proposalApi.post(`/proposal/${quoteId}/create-paylink`)
  try {
    return payLinkSchema.nullable().parse(res.data)
  } catch (error) {
    if (error instanceof ZodError) {
      trackException(error, { context: 'proposal.createPaylinkForFirstMilestone.validation' })
      throw new Error('There was an issue creating the payment link. Please refresh the page and try again.')
    }
    throw error
  }
}

/** proposal-api gates create-paylink on prvReadnodus_gateway (the Nodus -
 * ePayment Processor role). Reading a gateway checks the same privilege, so
 * only users who can use the button see it. 403 is an answer, not an error. */
export async function getCanCreatePaylink() {
  const res = await dynamicsApi.get('/nodus_gatewaies?$select=nodus_gatewayid&$top=1', {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 403,
  })
  return res.status !== 403
}

export function useCanCreatePaylinkQuery(enabled: boolean) {
  return useQuery({
    queryKey: QUERY_KEYS.canCreatePaylink,
    queryFn: getCanCreatePaylink,
    enabled,
    staleTime: Infinity,
  })
}

export async function createPaylinkForMilestones(milestoneIds: string[]) {
  const quoteId = getQuoteId()
  const res = await proposalApi.post(`/proposal/${quoteId}/create-paylink-for-milestones`, { milestoneIds })
  try {
    return payLinkSchema.nullable().parse(res.data)
  } catch (error) {
    if (error instanceof ZodError) {
      trackException(error, { context: 'proposal.createPaylinkForMilestones.validation' })
      throw new Error('There was an issue creating the payment link. Please refresh the page and try again.')
    }
    throw error
  }
}

export async function reopenQuote(quote: Pick<QuoteData, 'quoteid' | '@odata.etag'>) {
  await proposalApi.post(`/proposal/${quote.quoteid}/reopen`, undefined, { headers: quoteStatusHeaders(quote) })
}

export async function setQuoteToDraft(quote: Pick<QuoteData, 'quoteid' | '@odata.etag'>) {
  await proposalApi.post(`/quote/${quote.quoteid}/draft`, undefined, { headers: quoteStatusHeaders(quote) })
}

export async function closeQuoteAsLost(quote: Pick<QuoteData, 'quoteid' | '@odata.etag'>) {
  return (await proposalApi.post(`/quote/${quote.quoteid}/close-as-lost`, undefined, { headers: quoteStatusHeaders(quote) })).data
}

export async function clearProposalApproval(quote: QuoteData) {
  if (!quote?.quoteid) {
    throw new Error('Quote not found')
  }

  return await reopenQuote(quote)
}
